// @ts-strict-ignore
import { ArticleConnectionPropertyValue } from '@article/constants/article-connection-property-values';
import { ProjectModel } from '@project/model/project.model';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleManager } from '@article/manager/article.manager';
import { ArticleUtils } from '@article/util/article-utils';
import { CodingArticleUtils } from '@article/util/coding-article.utils';
import { UndoRedoManager } from '@shared/undo/undo-redo.manager';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { Timer } from '@shared/timer';
import { PropertyValueVO } from '../model/vo/property.vo';
import { PropertyValueType } from '../../../constants/property-value-type';
import { StringUtils } from '@shared/util/string-utils';
import { ProjectContentPresenter } from '@shared/presenter/project-content.presenter';
import { AlternativeReplaceArticleManager } from './alternative-replace-article.manager';
import { Injectable, OnDestroy } from '@angular/core';
import { lastValueFrom, Subscription } from 'rxjs';
import { SelectionModelService } from '../service/selection-model.service';
import { IsProjectType } from '../../../model/is-project-type';

@Injectable({ providedIn: 'root' })
export class CodingFingerArticleManager implements OnDestroy {

  public isActive: boolean = false;
  public updateProjectPropertiesTimer: Timer = new Timer(500);

  public articleListToReplace = [];

  public addedArticlesSubscription: Subscription;

  constructor(
    private projectModel: ProjectModel,
    private articleManager: ArticleManager,
    private undoRedoManager: UndoRedoManager,
    private selectionModelService: SelectionModelService,
    private projectContentPresenter: ProjectContentPresenter,
    private containerService: ContainerService,
    private compositeArticleUtils: CompositeArticleUtils,
    private codingArticleUtils: CodingArticleUtils,
  ) {}

  public ngOnDestroy(): void {
    this.addedArticlesSubscription?.unsubscribe();
    this.addedArticlesSubscription = undefined;
  }

  public finishAttachDetachOperation() {
    this.selectionModelService.removeAllSelections();
    this.undoRedoManager.finalizeUndoOperationSequence();
  }

  public async replaceArticle(alternativeReplaceArticleManager : AlternativeReplaceArticleManager, articleUsage: ArticleUsageVO, newArticleId: number): Promise<void> {
    let replaceArticleUsageId = articleUsage.id;
    if (ArticleUsageUtils.isCompositePartArticleUsage(articleUsage)) {
      replaceArticleUsageId = this.compositeArticleUtils.getBaseCompositePartArticlesByArticleUsage(articleUsage)[0].articleUsage.id;
    }
    const article = await lastValueFrom(this.articleManager.getArticles([newArticleId]));
    if (alternativeReplaceArticleManager !== null) {
      await alternativeReplaceArticleManager.replaceArticlesByUsageIds([replaceArticleUsageId], article[0]);
    } else {
      throw new Error("AlternativeReplaceArticleManager does not exist yet");
    }
  }

  public isCodingFingerHandlingEnabled(): boolean {
    return ProjectUtils.canRemoveCodingFingerinFreeMountingOrMCS(this.projectModel);
  }

  public queueProjectPropertyUpdate(articleUsage: ArticleUsageVO): void {
    const compositeArticleId: number = ArticleUsageUtils.getArticleUsageInfoPropertyIntValue(
      articleUsage,
      ArticleUsageInfoPropertyDef.STRIPPED_COMPOSITE_ARTICLE_ID
    );
    const compositeArticleVersion: number = ArticleUsageUtils.getArticleUsageInfoPropertyIntValue(
      articleUsage,
      ArticleUsageInfoPropertyDef.STRIPPED_COMPOSITE_ARTICLE_VERSION
    );
    const compositeArticleVO: ArticleVO = this.articleManager.getCachedArticle(compositeArticleId, compositeArticleVersion);
    if (!isNaN(compositeArticleId) && !isNaN(compositeArticleVersion) && !compositeArticleVO) {
      this.articleManager.loadFullArticle(compositeArticleId, compositeArticleVersion).subscribe(
        (article: ArticleVO) => {
          if (article) {
            this.loadArticleHandler_Success();
          } else {
            this.loadArticleHandler_Failure();
          }
        },
        (error) => this.loadArticleHandler_Failure()
      );
    } else {
      this.continueQueueProjectPropertyUpdate();
    }
  }

  public loadArticleHandler_Failure(): void {
    this.continueQueueProjectPropertyUpdate();
  }

  public loadArticleHandler_Success(): void {
    this.continueQueueProjectPropertyUpdate();
  }

  public continueQueueProjectPropertyUpdate(): void {
    if (!this.updateProjectPropertiesTimer.hasEventListener(Timer.TIMER_EVENT)) {
      this.updateProjectPropertiesTimer.addEventListener(Timer.TIMER_EVENT, () => this.updateProjectPropertiesTimerHandler());
    }
    this.updateProjectPropertiesTimer.reset();
    this.updateProjectPropertiesTimer.start();
  }

  public updateRemovedCodingFingerPositionsProjectInfoProperty(): void {
    const positions: string[] = [];
    const compositeUsageIds: string[] = [];
    const compositePartUsages: ArticleUsageVO[] = []; // array containing one part of each composite article
    this.containerService
      .enumerateArticleContainersInGlobal()
      .filter((a) => !CodingArticleUtils.isCodingFinger(a.article) && !ArticleUtils.isContact(a.article))
      .forEach((a) => {
        let compositeUsageId: string = ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(
          a.articleUsage,
          ArticleUsageInfoPropertyDef.STRIPPED_COMPOSITE_ARTICLE_USAGE_ID
        );
        if (StringUtils.isEmpty(compositeUsageId)) {
          compositeUsageId = ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(
            a.articleUsage,
            ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_USAGE_ID
          );
        }
        if (StringUtils.isNotEmpty(compositeUsageId) && compositeUsageIds.indexOf(compositeUsageId) === -1) {
          compositeUsageIds.push(compositeUsageId);
          compositePartUsages.push(a.articleUsage);
        }
      });
    // collect removed contact/coding finger positions from each composite article
    compositePartUsages.forEach((u) => positions.push(...this.getRemovedCodingFingerPositions(u)));
    positions.sort((a: string, b: string) => StringUtils.stringCompare(a.toString(), b.toString(), true));
    const resultPropertyValues: PropertyValueVO[] = [];
    for (const position of positions) {
      const newPropertyValueVO = new PropertyValueVO();
      newPropertyValueVO.type = PropertyValueType.STRING;
      newPropertyValueVO.stringValue = position + '';
      resultPropertyValues.push(newPropertyValueVO);
    }
    this.projectModel.currentInfoProperties.setMultiple("brokenCodingFingerPositions", resultPropertyValues);
  }

  private getRemovedCodingFingerPositions(articleUsage: ArticleUsageVO): string[] {
    const article: ArticleVO = this.articleManager.getCachedArticle(articleUsage.articleId, articleUsage.articleVersion);
    const removedCodingFingerPositions: string[] = this.getRemovedPositions(
      article,
      articleUsage,
      ArticleConnectionPropertyValue.CODING_FINGER_CONNECTION_TYPE
    );
    const removedContactPositions: string[] = this.getRemovedPositions(
      article,
      articleUsage,
      ArticleConnectionPropertyValue.CONTACT_CONNECTION_TYPE
    );
    return removedCodingFingerPositions.concat(removedContactPositions);
  }

  public updateProjectPropertiesTimerHandler(): void {
    this.updateRemovedCodingFingerPositionsProjectInfoProperty();
  }

  public getRemovedPositions(article: ArticleVO, articleUsage: ArticleUsageVO, type: string): string[] {
    let compositeArticleId: number = ArticleUsageUtils.getArticleUsageInfoPropertyIntValue(
      articleUsage,
      ArticleUsageInfoPropertyDef.STRIPPED_COMPOSITE_ARTICLE_ID
    );
    let compositeArticleVersion: number = ArticleUsageUtils.getArticleUsageInfoPropertyIntValue(
      articleUsage,
      ArticleUsageInfoPropertyDef.STRIPPED_COMPOSITE_ARTICLE_VERSION
    );
    let useStrippedInfo: boolean = true;
    if (isNaN(compositeArticleId) || isNaN(compositeArticleVersion)) {
      useStrippedInfo = false;
      compositeArticleId = ArticleUsageUtils.getArticleUsageInfoPropertyIntValue(articleUsage, ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_ID);
      compositeArticleVersion = ArticleUsageUtils.getArticleUsageInfoPropertyIntValue(
        articleUsage,
        ArticleUsageInfoPropertyDef.COMPOSITE_ARTICLE_VERSION
      );
    }
    const compositeArticleVO: ArticleVO = this.articleManager.getCachedArticle(compositeArticleId, compositeArticleVersion);
    const compositePartContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(articleUsage.id);
    const compositePartUsages: ArticleUsageVO[] = this.containerService.getCompositePartArticleUsages(compositePartContainer, useStrippedInfo);
    let codingFingerList: any[] = null;

    if (IsProjectType.mcsGds(this.projectModel.currentProject)) {
      codingFingerList = this.codingArticleUtils.getCodingFingerList(compositePartUsages, compositeArticleVO, type, this.projectContentPresenter);
    } else {
      codingFingerList = this.codingArticleUtils.getCodingFingerList(compositePartUsages, compositeArticleVO, type, null);
    }
    const removedCodingFingerPositions: string[] = [];
    for (const cfo of codingFingerList) {
      // check that connection is empty
      if (!cfo.isInUse) {
        // check that conncetion properties actually contain a removed article id
        if (cfo.removedCodingFingerArticleId) {
          removedCodingFingerPositions.push(cfo.positionLabel);
        }
      }
    }
    return removedCodingFingerPositions;
  }

  public getBackwardConnectedArticleUsage(articleUsage: ArticleUsageVO): ArticleUsageVO {
    if (!articleUsage) {
      return null;
    }
    const parentArticleUsageId: string = ArticleUsageUtils.getFirstBackwardConnectedArticleUsageId(articleUsage);
    return this.projectModel.getArticleUsage(parentArticleUsageId);
  }
}
